// src/services/appConfig.js
//
// Gestiona configuración de la app que vive en Firestore (app_config/*),
// con cache módulo-nivel para lecturas síncronas desde código no-async
// como callGemini.
//
// Modelo: 1 listener global se suscribe al doc al iniciar la app. Cuando
// el super-admin guarda una nueva clave desde el Admin Panel, el listener
// dispara y el cache se actualiza en TODAS las pestañas/usuarios sin
// necesidad de recargar.

import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const GEMINI_DOC_PATH = ['app_config', 'gemini'];

let cachedGeminiKey = null;     // null = aún no leído o doc no existe
let unsubscribe = null;
let initialized = false;

export const initAppConfig = () => {
  if (initialized) return;
  initialized = true;
  const ref = doc(db, ...GEMINI_DOC_PATH);
  unsubscribe = onSnapshot(
    ref,
    (snap) => {
      cachedGeminiKey = snap.exists() ? (snap.data().apiKey || null) : null;
    },
    (err) => {
      console.error('[appConfig] Error en suscripción a app_config/gemini:', err);
    }
  );
};

export const stopAppConfig = () => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
    initialized = false;
  }
};

// Devuelve la clave de Gemini activa. Sync: usa el cache.
// Fallback: si Firestore no tiene clave, usa la del build (.env).
export const getGeminiKey = () => {
  return cachedGeminiKey || process.env.REACT_APP_GEMINI_API_KEY || '';
};

// Indica de dónde viene la clave activa. Útil para mostrar badge en la UI.
export const getGeminiKeySource = () => {
  if (cachedGeminiKey) return 'firestore';
  if (process.env.REACT_APP_GEMINI_API_KEY) return 'env';
  return 'none';
};

// Solo super-admin pasa las reglas de Firestore.
export const saveGeminiKey = async (apiKey, userEmail) => {
  const trimmed = (apiKey || '').trim();
  if (!trimmed) throw new Error('La clave no puede estar vacía');
  const ref = doc(db, ...GEMINI_DOC_PATH);
  await setDoc(
    ref,
    {
      apiKey: trimmed,
      updatedAt: serverTimestamp(),
      updatedBy: userEmail || 'unknown',
    },
    { merge: true }
  );
};

// Suscribe a metadata del doc (apiKey, updatedAt, updatedBy) para UI.
// Devuelve la función de unsubscribe.
export const subscribeToGeminiKeyMeta = (callback) => {
  const ref = doc(db, ...GEMINI_DOC_PATH);
  return onSnapshot(
    ref,
    (snap) => {
      callback(snap.exists() ? snap.data() : null);
    },
    (err) => {
      console.error('[appConfig] Error suscripción meta:', err);
      callback(null);
    }
  );
};
